import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles, FileText, Quote, TriangleAlert, ScrollText } from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { fmtDate, SOURCE_LABEL, ANALYSIS_STATUS_META, VERDICT_LABEL } from '@/lib/format';
import { FeedbackControl } from './FeedbackControl';
import { StrengthBadge, ClassificationBadge } from '@/components/opportunities/OppBadges';
import { toast } from 'sonner';

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const Highlighted = ({ text, quotes }) => {
  const clean = (quotes || []).map((q) => (q.quote || '').trim()).filter((q) => q.length > 3);
  if (!text) return <span className="text-slate-400">No extracted text.</span>;
  if (clean.length === 0) return <span>{text}</span>;
  const re = new RegExp(`(${clean.map(escapeRe).join('|')})`, 'gi');
  const parts = text.split(re);
  return (
    <span>
      {parts.map((part, i) => (
        clean.some((q) => q.toLowerCase() === part.toLowerCase())
          ? <mark key={i} className="bg-amber-200/70 rounded px-0.5">{part}</mark>
          : <span key={i}>{part}</span>
      ))}
    </span>
  );
};

const Field = ({ label, children }) => (
  <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">{label}</div>
    <div className="text-sm text-slate-700 leading-relaxed">{children}</div></div>
);

export const EventReviewDrawer = ({ eventId, open, onClose, onChanged, onDraftCO, draftingId }) => {
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    if (!eventId) return;
    setLoading(true);
    api.get(`/events/${eventId}`).then((r) => setEv(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { if (open && eventId) load(); }, [open, eventId]);

  const det = ev?.detection;
  const meta = ev ? ANALYSIS_STATUS_META[ev.analysis_status] || ANALYSIS_STATUS_META.pending : null;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="event-review-drawer">
        {loading || !ev ? (
          <div className="flex items-center justify-center h-full"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div>
            <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{SOURCE_LABEL[ev.source_type] || ev.source_type}</span>
                {meta && <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full', meta.cls)} data-testid="analysis-status-badge">{meta.label}</span>}
                {ev.truncated_for_ai && <span className="text-[10px] text-amber-700">truncated for AI</span>}
              </div>
              <SheetTitle className="text-base mt-1">{ev.subject || ev.ai_title || ev.file_name || 'Commercial event'}</SheetTitle>
              <div className="text-xs text-slate-400">{ev.sender} &middot; {fmtDate(ev.timestamp)}{ev.file_name ? ` · ${ev.file_name}` : ''}</div>
            </SheetHeader>

            <div className="px-5 py-4 space-y-5">
              {ev.analysis_status === 'failed' && (
                <div className="rounded-lg bg-danger-50 text-danger-700 text-sm p-3 flex gap-2"><TriangleAlert className="h-4 w-4 mt-0.5" />AI analysis failed: {ev.analysis_error}</div>
              )}

              {(ev.analysis_status === 'scope_change' || ev.analysis_status === 'no_change') && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="text-sm font-semibold text-slate-800">What Recoup found</div>
                    <span className={cn('text-xs font-semibold px-2 py-0.5 rounded-full', meta.cls)}>{ev.is_scope_change ? 'Opportunity found' : 'Nothing to claim'}</span>
                    <span className="ml-auto"><StrengthBadge confidence={ev.ai_confidence} /></span>
                  </div>
                  {ev.ai_summary && <Field label="What we found">{ev.ai_summary}</Field>}
                  {det?.commercial_reasoning && <Field label="Why it matters">{det.commercial_reasoning}</Field>}
                  {det?.contractual_basis && <Field label="Contractual basis">{det.contractual_basis}</Field>}
                  {det && (
                    <div className="flex flex-wrap items-center gap-2">
                      <ClassificationBadge classification={det.classification} testid="event-classification" />
                      <span className="text-[11px] text-slate-500">Amounts are never AI-guessed — price this in the opportunity's deterministic calculator.</span>
                    </div>
                  )}
                  {det?.evidence_quotes?.length > 0 && (
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Supporting evidence</div>
                      <div className="space-y-2">
                        {det.evidence_quotes.map((q, i) => (
                          <div key={i} className="rounded-lg bg-amber-50 border border-amber-100 p-2.5">
                            <div className="text-sm text-slate-800 flex gap-1.5"><Quote className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />“{q.quote}”</div>
                            {q.why && <div className="text-[11px] text-slate-500 mt-1 ml-5">{q.why}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {det && det.status === 'new' && onDraftCO && (
                    <Button className="w-full bg-money hover:bg-money-700" disabled={draftingId === det.id}
                      onClick={() => onDraftCO && onDraftCO(det)} data-testid="drawer-draft-co">
                      {draftingId === det.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Draft Change Order
                    </Button>
                  )}
                </div>
              )}

              <details className="rounded-lg border border-slate-200">
                <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-slate-600 flex items-center gap-1.5"><ScrollText className="h-3.5 w-3.5" />Extracted text (source of truth)</summary>
                <div className="px-3 pb-3 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto" data-testid="event-extracted-text">
                  <Highlighted text={ev.content} quotes={det?.evidence_quotes} />
                </div>
              </details>

              <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/60">
                <div className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5"><FileText className="h-4 w-4" />Your feedback</div>
                {ev.feedback?.length > 0 && (
                  <div className="mb-3 space-y-2">
                    {ev.feedback.map((f) => (
                      <div key={f.id} className="text-xs bg-white rounded-lg border border-slate-200 px-3 py-2">
                        <span className="font-semibold text-slate-700">{VERDICT_LABEL[f.verdict]}</span>
                        {f.comment && <span className="text-slate-500"> — “{f.comment}”</span>}
                        <span className="text-slate-400 float-right">{fmtDate(f.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {ev.analysis_status === 'scope_change' || ev.analysis_status === 'no_change' ? (
                  <FeedbackControl event={ev} onSubmitted={() => { load(); onChanged && onChanged(); }} />
                ) : (
                  <div className="text-xs text-slate-400">Feedback becomes available once the event has been analyzed.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
